import { ApiResponse, PaginatedResponse } from "../types/common";
import api from "./api";

export interface Notice {
  _id: string;
  title: string;
  content: string;
  type: "urgent" | "important" | "academic" | "info" | "general";
  priority: "high" | "medium" | "low";
  targetAudience: ("all" | "students" | "teachers" | "staff" | "parents")[];
  courseId?: string;
  author: {
    userId:
      | string
      | {
          _id: string;
          firstName: string;
          lastName: string;
        };
    name: string;
    department: string;
  };
  isActive: boolean;
  publishDate: string;
  expiryDate?: string;
  isRead?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNoticeRequest {
  title: string;
  content: string;
  type?: "urgent" | "important" | "academic" | "info" | "general";
  priority?: "high" | "medium" | "low";
  targetAudience?: ("all" | "students" | "teachers" | "staff" | "parents")[];
  courseId?: string;
  publishDate?: string;
  expiryDate?: string;
}

export interface NoticeFilter {
  page?: number;
  limit?: number;
  type?: string;
  priority?: string;
  targetAudience?: string;
}

// Get notices for student
export const getStudentNotices = async (
  page = 1,
  limit = 10,
): Promise<ApiResponse<Notice[]>> => {
  try {
    const response = await api.get(
      `/notices/student?page=${page}&limit=${limit}`,
    );
    return {
      success: (response as any).success,
      data: (response as any).data || [],
      message: (response as any).message,
      pagination: (response as any).pagination,
    };
  } catch (error) {
    console.error("Error fetching student notices:", error);
    return {
      success: false,
      data: [],
      message: "Failed to fetch notices",
    };
  }
};

// Get notices for teacher (can see notices they created and notices targeted at them)
export const getTeacherNotices = async (
  filters: NoticeFilter = {},
): Promise<PaginatedResponse<Notice>> => {
  try {
    const params = new URLSearchParams();

    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());

    const response = await api.get(`/notices?${params.toString()}`);
    const data = response as any;

    return {
      success: data.success,
      data: data.data || [],
      pagination: {
        currentPage: data.pagination?.page || 1,
        totalPages: data.pagination?.pages || 1,
        totalItems: data.pagination?.total || 0,
        hasNext: (data.pagination?.page || 1) < (data.pagination?.pages || 1),
        hasPrev: (data.pagination?.page || 1) > 1,
      },
    };
  } catch (error) {
    console.error("Error fetching teacher notices:", error);
    return {
      success: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNext: false,
        hasPrev: false,
      },
    };
  }
};

// Mark notice as read
export const markNoticeAsRead = async (
  noticeId: string,
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/notices/${noticeId}/read`);
    return {
      success: (response as any).success,
      data: undefined,
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error marking notice as read:", error);
    return {
      success: false,
      data: undefined,
      message: "Failed to mark notice as read",
    };
  }
};

// Hide notice for current user
export const hideNotice = async (
  noticeId: string,
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/notices/${noticeId}/hide`);
    return {
      success: (response as any).success,
      data: undefined,
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error hiding notice:", error);
    return {
      success: false,
      data: undefined,
      message: "Failed to hide notice",
    };
  }
};

// Unhide notice for current user
export const unhideNotice = async (
  noticeId: string,
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/notices/${noticeId}/unhide`);
    return {
      success: (response as any).success,
      data: undefined,
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error unhiding notice:", error);
    return {
      success: false,
      data: undefined,
      message: "Failed to unhide notice",
    };
  }
};

// Get all notices (admin)
export const getAllNotices = async (
  filters: NoticeFilter = {},
): Promise<PaginatedResponse<Notice>> => {
  try {
    const params = new URLSearchParams();

    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());
    if (filters.type) params.append("type", filters.type);
    if (filters.priority) params.append("priority", filters.priority);
    if (filters.targetAudience)
      params.append("targetAudience", filters.targetAudience);

    const response = await api.get(`/notices?${params.toString()}`);
    const data = response as any;

    return {
      success: data.success,
      data: data.data || [],
      pagination: {
        currentPage: data.pagination?.page || 1,
        totalPages: data.pagination?.pages || 1,
        totalItems: data.pagination?.total || 0,
        hasNext: (data.pagination?.page || 1) < (data.pagination?.pages || 1),
        hasPrev: (data.pagination?.page || 1) > 1,
      },
    };
  } catch (error) {
    console.error("Error fetching all notices:", error);
    return {
      success: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNext: false,
        hasPrev: false,
      },
    };
  }
};

// Create notice (admin)
export const createNotice = async (
  data: CreateNoticeRequest,
): Promise<ApiResponse<Notice>> => {
  try {
    const response = await api.post("/notices", data);
    return {
      success: (response as any).success,
      data: (response as any).data,
      message: (response as any).message,
    };
  } catch (error: any) {
    console.error("Error creating notice:", error);
    console.error("Error details:", error.response?.data);
    return {
      success: false,
      data: {} as Notice,
      message: error.response?.data?.message || "Failed to create notice",
    };
  }
};

// Update notice (admin)
export const updateNotice = async (
  noticeId: string,
  data: Partial<CreateNoticeRequest>,
): Promise<ApiResponse<Notice>> => {
  try {
    const response = await api.put(`/notices/${noticeId}`, data);
    return {
      success: (response as any).success,
      data: (response as any).data,
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error updating notice:", error);
    return {
      success: false,
      data: {} as Notice,
      message: "Failed to update notice",
    };
  }
};

// Delete notice (admin)
export const deleteNotice = async (
  noticeId: string,
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete(`/notices/${noticeId}`);
    return {
      success: (response as any).success,
      data: undefined,
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error deleting notice:", error);
    return {
      success: false,
      data: undefined,
      message: "Failed to delete notice",
    };
  }
};
